// src/store/slices/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authAPI } from "@/services/auth";
import api, { setAccessToken, clearAccessToken } from "@/services/api";

// LOGIN
export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      // Store access token in memory (not localStorage)
      if (response.data?.token) {
        setAccessToken(response.data.token);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Login failed" });
    }
  }
);

// REGISTER
export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Registration failed" });
    }
  }
);

/**
 * fetchUserProfile — called on app mount to check if user is authenticated
 * Also handles silent refresh via the cookie-based refresh token
 */
export const fetchUserProfile = createAsyncThunk(
  "auth/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const userRes = await api.get("/auth/me");
      const statsRes = await api.get("/users/me/stats");

      const user =
        userRes?.data?.data?.user || userRes?.data?.user || userRes?.data;
      const stats =
        statsRes?.data?.data?.stats || statsRes?.data?.stats || statsRes?.data;

      return { user, stats };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load profile" });
    }
  }
);

/**
 * Silent refresh — attempt to get a new access token using the cookie
 * Called on app mount to restore session after page refresh
 */
export const silentRefresh = createAsyncThunk(
  "auth/silentRefresh",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.refreshToken();
      const token = response?.data?.token;
      if (token) {
        setAccessToken(token);
        return response;
      }
      return rejectWithValue({ message: "No token in refresh response" });
    } catch (err) {
      clearAccessToken();
      return rejectWithValue(err.response?.data || { message: "Session expired" });
    }
  }
);

// UPDATE EMAIL
export const updateUserEmail = createAsyncThunk(
  "auth/updateEmail",
  async ({ email, current_password }, { rejectWithValue }) => {
    try {
      const response = await api.put("/users/me/email", {
        email,
        current_password,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to update email" });
    }
  }
);

// UPDATE PASSWORD
export const updateUserPassword = createAsyncThunk(
  "auth/updatePassword",
  async ({ current_password, new_password, confirm_password }, { rejectWithValue }) => {
    try {
      const response = await api.put("/users/me/password", {
        current_password,
        new_password,
        confirm_password,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to update password" });
    }
  }
);

// DELETE ACCOUNT
export const deleteUserAccount = createAsyncThunk(
  "auth/deleteAccount",
  async ({ password }, { rejectWithValue }) => {
    try {
      const response = await api.delete("/users/me", {
        data: { password },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to delete account" });
    }
  }
);

// LOGOUT (server-side revoke + clear)
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Logout should succeed even if server call fails
    }
    clearAccessToken();
    return null;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: null,
    stats: null,
    isAuthenticated: false,
    isLoading: true, // Start as true to allow silent refresh check
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.stats = null;
      state.isAuthenticated = false;
      clearAccessToken();
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // LOGIN
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user =
          action.payload.data?.user ||
          action.payload.data?.data?.user ||
          action.payload.data?.data;
        state.token = action.payload.data?.token;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Login failed";
      })

      // REGISTER
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.data?.user || null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Registration failed";
      })

      // SILENT REFRESH
      .addCase(silentRefresh.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(silentRefresh.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload?.data?.token;
        state.user = action.payload?.data?.user;
        state.isAuthenticated = true;
      })
      .addCase(silentRefresh.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })

      // fetchUserProfile
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.stats = action.payload.stats;
        state.isAuthenticated = true;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load profile";

        if (action.payload?.status === 401) {
          state.user = null;
          state.stats = null;
          state.token = null;
          state.isAuthenticated = false;
          clearAccessToken();
        }
      })

      // LOGOUT (async thunk)
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.stats = null;
        state.isAuthenticated = false;
      })

      // UPDATE EMAIL
      .addCase(updateUserEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update user email in state if returned
        if (action.payload?.user?.email) {
          state.user = { ...state.user, email: action.payload.user.email };
        }
      })
      .addCase(updateUserEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to update email";
      })

      // UPDATE PASSWORD
      .addCase(updateUserPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to update password";
      })

      // DELETE ACCOUNT
      .addCase(deleteUserAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUserAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.stats = null;
        state.token = null;
        state.isAuthenticated = false;
        clearAccessToken();
      })
      .addCase(deleteUserAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to delete account";
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
